import { useEffect } from "react";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { getConsolidationGroupDisplayName } from "@common/helpers/entity";
import { Entity } from "@common/types/entity";
import { useEntities } from "@hooks/useEntities";
import { AccountingReportFilters } from "./types";
import { AccountingFiltersSetter } from "./useAccountingTab";
import { useReportConsolidationGroup } from "./useReportConsolidationGroup";

interface ConsolidatedReportView {
    consolidationGroup: ConsolidationGroupResponse | undefined;
    parentEntity: Entity | undefined;
    isConsolidated: boolean;
    displayEntityName: string | undefined;
    isResolving: boolean;
}

interface Params {
    filters: AccountingReportFilters;
    setFilters: AccountingFiltersSetter;
}

/**
 * Shared P&L / Balance Sheet consolidated-mode state for the report pages.
 * Resolves the active consolidation group, decides whether the current request
 * is consolidated, and clears a stale `consolidationGroupId` (e.g. flag turned
 * off, or the group was archived/deleted) so the user is never locked into
 * consolidated mode without a way out.
 */
export function useConsolidatedReportView({
    filters,
    setFilters,
}: Params): ConsolidatedReportView {
    const { consolidationGroup, isResolving } = useReportConsolidationGroup();
    const { consolidationGroupId } = filters;
    const entities = useEntities();
    const parentEntity = entities.find(
        ({ uuid }) => uuid === consolidationGroup?.parentEntityUuid,
    );

    const isConsolidated =
        Boolean(consolidationGroup) &&
        consolidationGroupId === consolidationGroup?.id;

    useEffect(() => {
        if (isResolving) {
            return;
        }
        const hasStaleGroupId =
            Boolean(consolidationGroupId) &&
            consolidationGroupId !== consolidationGroup?.id;
        if (hasStaleGroupId) {
            setFilters((currentFilters) => ({
                ...currentFilters,
                consolidationGroupId: undefined,
            }));
        }
    }, [isResolving, consolidationGroup, consolidationGroupId, setFilters]);

    return {
        consolidationGroup,
        parentEntity,
        isConsolidated,
        isResolving,
        displayEntityName:
            isConsolidated && consolidationGroup
                ? `${getConsolidationGroupDisplayName({
                      groupName: consolidationGroup.name,
                      parentEntityName: parentEntity?.name,
                  })} (Consolidated)`
                : undefined,
    };
}
